import { useMemo } from 'react';

import { useExamLocationRequestForm } from './exam-location-request-form-provider';

/**
 * Hook to get display-friendly data for readonly views.
 * Derives display data from form state and GraphQL responses.
 *
 * @pattern Separation of Concerns
 * - Use `useExamLocationRequestForm()` for form logic (editing/validation)
 * - Use `useExamLocationRequestDisplayData()` for readonly display
 *
 * @why
 * - Keeps the main context API clean and focused
 * - Opt-in computation (only components that need it pay the cost)
 * - Single source of truth for display logic
 * - Easy to extend without cluttering the main provider
 *
 */
export function useExamLocationRequestDisplayData() {
  const { form, provinceOptions, locationOptions, buildingOptions } = useExamLocationRequestForm();

  const formValues = form.watch();

  const displayData = useMemo(() => {
    const selectedProvince = provinceOptions.find(
      (option) => option.value === formValues.provinceId?.value,
    );
    const selectedLocation = locationOptions.find(
      (option) => option.value === formValues.locationId?.value,
    );
    const selectedBuilding = buildingOptions.find(
      (option) => option.value === formValues.buildingId?.value,
    );

    return {
      // Province
      province: selectedProvince
        ? {
            id: selectedProvince.value as string,
            nameTh: selectedProvince.label,
          }
        : null,

      // Location (can be existing or new)
      location: formValues.locationId
        ? selectedLocation
          ? {
              id: selectedLocation.value as string,
              nameTh: selectedLocation.label,
              nameEn: selectedLocation.nameEn ?? null,
            }
          : null
        : formValues.locationNameTh
          ? {
              nameTh: formValues.locationNameTh,
              nameEn: formValues.locationNameEn || null,
            }
          : null,

      // Building (can be existing or new)
      building: formValues.buildingId
        ? selectedBuilding
          ? {
              id: selectedBuilding.value as string,
              name: selectedBuilding.label,
            }
          : null
        : formValues.buildingName
          ? {
              name: formValues.buildingName,
            }
          : null,
    };
  }, [formValues, provinceOptions, locationOptions, buildingOptions]);

  return displayData;
}
